/**
 * Warns before a tab close / reload that would kill an in-flight batch file operation.
 *
 * Renders nothing. Mounted ABOVE the routed subtree (see common-app/pages/RootRouter) so
 * it outlives navigation, because the batch saga does too: batch trash/move/status runs
 * under caseMediaSaga, forked by the initMainPage route saga that the routing layer now
 * preserves across the side-menu routes (VE-26729).
 *
 * This guard used to live inside CaseMedia/MultiSelectBar, which is mounted only by the
 * folder-view Home page. That was fine when the nav drawer hard-reloaded the browser —
 * leaving Home *was* the unload, and a separate confirm dialog covered it. Once the drawer
 * became a real SPA navigation, MultiSelectBar unmounted while the batch kept running,
 * taking the beforeunload listener with it and leaving the operation unguarded on exactly
 * the path the change introduced. Hence: one owner, route-independent, keyed on the
 * casemedia store's batchOperation rather than on any page being mounted.
 *
 * Scope, precisely: this covers tab close and reload. It does NOT cover navigating to a
 * route whose saga differs from initMainPage — an editor, say — which still cancels
 * caseMediaSaga and kills the batch partway with no warning. That gap predates VE-26729
 * and is not addressed here.
 */
import { useEffect } from 'react';
import { useSelector } from 'react-redux';

import { selectBatchOperation } from '@redact-modules/casemedia/selectors';

const BatchOperationUnloadGuard = () => {
  const batchOperation = useSelector(selectBatchOperation);
  const isBatchInProgress = Boolean(batchOperation);

  useEffect(() => {
    if (!isBatchInProgress) {
      return;
    }

    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      // For browser compatibility (older browsers need returnValue set); the custom text
      // is ignored by modern browsers, which show a generic prompt.
      // eslint-disable-next-line @typescript-eslint/no-deprecated
      e.returnValue = '';
    };

    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isBatchInProgress]);

  return null;
};

export default BatchOperationUnloadGuard;
